import React from 'react';
import type { RequestsQueryState } from '../lib/adminRequests';
import type {
  AdminApiError,
  AdminTicketCategory,
  AdminTicketListItemDto,
  AdminTicketListResponse,
  AdminTicketStatus,
} from '../types';
import { AdminButton, StatusPill } from './components';

const StatusOptions: Array<{ label: string; value: AdminTicketStatus | null }> = [
  { label: 'All', value: null },
  { label: 'New', value: 'new' },
  { label: 'Open', value: 'open' },
  { label: 'Resolved', value: 'resolved' },
];
const CategoryOptions: AdminTicketCategory[] = ['Bug', 'Question', 'Billing', 'ErrorReport'];

const countForStatus = (
  counts: AdminTicketListResponse['statusCounts'],
  status: AdminTicketStatus | null
) => status ? counts[status] : counts.new + counts.open + counts.resolved;

const countForCategory = (
  counts: AdminTicketListResponse['categoryCounts'],
  category: AdminTicketCategory
) => counts[category[0].toLowerCase() + category.slice(1) as keyof typeof counts];

const formatDate = (value: string) => new Intl.DateTimeFormat('en-GB', {
  dateStyle: 'medium',
  timeStyle: 'short',
}).format(new Date(value));

const RequestPill: React.FC<{
  children: React.ReactNode;
  tone?: 'info' | 'ok' | 'warn' | 'crit';
}> = ({ children, tone = 'info' }) => (
  <span className={`admin-requests-pill admin-requests-pill--${tone}`}>{children}</span>
);

const TicketListItem: React.FC<{
  active: boolean;
  ticket: AdminTicketListItemDto;
  onSelect: () => void;
}> = ({ active, ticket, onSelect }) => (
  <button
    aria-current={active ? 'true' : undefined}
    className={`requests-list__item${active ? ' requests-list__item--active' : ''}`}
    onClick={onSelect}
    type="button"
  >
    <span className="requests-list__rail" aria-hidden="true" />
    <span className="requests-list__head">
      <strong>{ticket.number}</strong>
      {ticket.status === 'new' && <span className="requests-list__new-dot" aria-label="New request" />}
      <time dateTime={ticket.updatedAt}>{formatDate(ticket.updatedAt)}</time>
    </span>
    <span className="requests-list__subject">{ticket.subject}</span>
    <span className="requests-list__preview">{ticket.lastMessagePreview}</span>
    <span className="requests-list__meta">
      <RequestPill>{ticket.category === 'ErrorReport' ? 'Error report' : ticket.category}</RequestPill>
      <RequestPill tone={ticket.priority === 'high' ? 'crit' : ticket.priority === 'medium' ? 'warn' : 'ok'}>
        {ticket.priority}
      </RequestPill>
      <StatusPill tone={ticket.status === 'resolved' ? 'success' : ticket.status === 'new' ? 'warning' : 'info'}>
        {ticket.status}
      </StatusPill>
    </span>
  </button>
);

interface RequestsInboxProps {
  categoryCounts: AdminTicketListResponse['categoryCounts'];
  draftSearch: string;
  error: AdminApiError | null;
  listRef: React.RefObject<HTMLDivElement | null>;
  loading: boolean;
  loadingMore: boolean;
  nextCursor: string | null;
  onDraftSearch: (value: string) => void;
  onLoadMore: () => void;
  onQueryPatch: (patch: Partial<RequestsQueryState>) => void;
  pageSentinelRef: React.RefObject<HTMLDivElement | null>;
  query: RequestsQueryState;
  statusCounts: AdminTicketListResponse['statusCounts'];
  tickets: AdminTicketListItemDto[];
}

type RequestFiltersProps = Pick<RequestsInboxProps,
  'categoryCounts' | 'draftSearch' | 'onDraftSearch' | 'onQueryPatch' | 'query' | 'statusCounts'>;

const RequestFilters: React.FC<RequestFiltersProps> = ({
  categoryCounts,
  draftSearch,
  onDraftSearch,
  onQueryPatch,
  query,
  statusCounts,
}) => (
  <>
    <label className="requests-search">
      Search requests
      <input
        aria-label="Search requests"
        onChange={(event) => onDraftSearch(event.target.value)}
        placeholder="Subject, user, company, number"
        value={draftSearch}
      />
    </label>
    <div className="requests-filter-row" aria-label="Status filters">
      {StatusOptions.map((status) => (
        <button
          className={query.status === status.value ? 'is-active' : ''}
          key={status.label}
          onClick={() => onQueryPatch({ status: status.value, ticket: query.ticket })}
          type="button"
        >
          {status.label} <span>{countForStatus(statusCounts, status.value)}</span>
        </button>
      ))}
    </div>
    <div className="requests-filter-row" aria-label="Category filters">
      {CategoryOptions.map((category) => (
        <button
          className={query.category === category ? 'is-active' : ''}
          key={category}
          onClick={() => onQueryPatch({
            category: query.category === category ? null : category,
            ticket: query.ticket,
          })}
          type="button"
        >
          {category === 'ErrorReport' ? 'Error report' : category}{' '}
          <span>{countForCategory(categoryCounts, category)}</span>
        </button>
      ))}
    </div>
  </>
);

type TicketItemsProps = Pick<RequestsInboxProps,
  'listRef' | 'loading' | 'loadingMore' | 'nextCursor' | 'onQueryPatch' |
  'pageSentinelRef' | 'query' | 'tickets'>;

const TicketItems: React.FC<TicketItemsProps> = ({
  listRef,
  loading,
  loadingMore,
  nextCursor,
  onQueryPatch,
  pageSentinelRef,
  query,
  tickets,
}) => (
  <div className="requests-list__items" ref={listRef}>
    {tickets.map((ticket) => (
      <TicketListItem
        active={query.ticket === ticket.id}
        key={ticket.id}
        onSelect={() => onQueryPatch({ ticket: ticket.id })}
        ticket={ticket}
      />
    ))}
    {!loading && tickets.length === 0 && (
      <p className="requests-empty">No requests match the current filters.</p>
    )}
    {loading && tickets.length === 0 && <p className="requests-loading">Loading requests…</p>}
    {nextCursor && (
      <div className="requests-page-sentinel" data-testid="requests-page-sentinel" ref={pageSentinelRef}>
        {loadingMore ? <p className="requests-loading" role="status">Loading older requests…</p> : null}
      </div>
    )}
  </div>
);

const RequestsInbox: React.FC<RequestsInboxProps> = ({
  categoryCounts,
  draftSearch,
  error,
  listRef,
  loading,
  loadingMore,
  nextCursor,
  onDraftSearch,
  onLoadMore,
  onQueryPatch,
  pageSentinelRef,
  query,
  statusCounts,
  tickets,
}) => (
  <section className="requests-list" aria-label="Requests inbox">
    <header className="requests-list__header">
      <div><p className="requests-list__eyebrow">Requests</p><h1>Requests inbox</h1></div>
      <button type="button" onClick={() => onQueryPatch({ category: 'ErrorReport', ticket: null })}>
        Error reports
      </button>
    </header>
    <RequestFilters
      categoryCounts={categoryCounts}
      draftSearch={draftSearch}
      onDraftSearch={onDraftSearch}
      onQueryPatch={onQueryPatch}
      query={query}
      statusCounts={statusCounts}
    />
    {error && <p className="requests-error" role="alert">{error.message}</p>}
    <TicketItems
      listRef={listRef}
      loading={loading}
      loadingMore={loadingMore}
      nextCursor={nextCursor}
      onQueryPatch={onQueryPatch}
      pageSentinelRef={pageSentinelRef}
      query={query}
      tickets={tickets}
    />
    {nextCursor && typeof IntersectionObserver === 'undefined' && (
      <AdminButton disabled={loadingMore} onClick={onLoadMore}>Load older requests</AdminButton>
    )}
  </section>
);

export default RequestsInbox;
